import { useMemo } from "react";

import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient } from "hooks";
import { TYRE_POSITIONS } from "models";
import { CarDetailsQueryKeys } from "modules/CarDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { WebSocketMessageListener } from "components";
import type { Car, CarNote, CarNoteAttachment, TruckTyres } from "models";

export interface CarWithGroupedTruckTyres extends Car {
  _groupedTruckTyres: TruckTyres[][];
  _spareIndex: number;
}

export const groupTruckTyres = (car: Car): CarWithGroupedTruckTyres => {
  const truck_tyres: TruckTyres[][] = [];

  car.truck_tyres?.forEach(tyre => (truck_tyres[tyre.axle - 1] = [...(truck_tyres[tyre.axle - 1] ?? []), tyre]));

  return { ...car, _groupedTruckTyres: truck_tyres, _spareIndex: truck_tyres.findIndex(group => group.some(t => t.position === TYRE_POSITIONS.TruckSpare)) };
};

export const useRealTimeCarQuery = (id: number) => {
  const listeners = useMemo(() => {
    const carListeners: WebSocketMessageListener<Car>[] = [
      {
        model: "Car",
        action: "upsert",
        id,
        callback: message => {
          const car = typedQueryClient.getQueryData(CarDetailsQueryKeys.details);
          if (!car) return;

          const carData = message.data;
          typedQueryClient.setQueryData(CarDetailsQueryKeys.details, { ...car, ...carData });
        }
      }
    ];

    const carNoteListeners: WebSocketMessageListener<CarNote>[] = [
      {
        model: "CarNote",
        action: "upsert",
        filter: { car_id: id },
        callback: message => {
          const car = typedQueryClient.getQueryData(CarDetailsQueryKeys.details);
          if (!car) return;

          const carNote = message.data;
          const carNotes = [...(car.notes ?? [])];
          const carNoteIdx = carNotes.findIndex(n => n.id === carNote.id);

          const updatedNotes = carNoteIdx === -1 ? [...carNotes, carNote] : car.notes?.with(carNoteIdx, { ...car.notes[carNoteIdx], ...carNote });
          typedQueryClient.setQueryData(CarDetailsQueryKeys.details, { ...car, notes: updatedNotes });
        }
      },
      {
        model: "CarNote",
        action: "delete",
        filter: { car_id: id },
        callback: message => {
          const car = typedQueryClient.getQueryData(CarDetailsQueryKeys.details);
          if (!car?.notes?.length) return;

          const carNote = message.data;
          const carNoteIdx = car.notes.findIndex(note => note.id === carNote.id);
          if (carNoteIdx < 0) return;

          typedQueryClient.setQueryData(CarDetailsQueryKeys.details, { ...car, notes: car.notes.toSpliced(carNoteIdx, 1) });
        }
      }
    ];

    const carNoteAttachmentListeners: WebSocketMessageListener<CarNoteAttachment>[] = [
      {
        model: "CarNoteAttachment",
        action: "delete",
        filter: { car_id: id },
        callback: ({ data: attachment }) => {
          const car = typedQueryClient.getQueryData(CarDetailsQueryKeys.details);
          if (!car?.notes?.length) return;

          const noteIdx = car.notes.findIndex(n => n.id === attachment.car_note_id);
          if (noteIdx < 0) return;

          const note = car.notes[noteIdx];
          const attachmentIdx = note.attachments?.findIndex(a => a.id === attachment.id) ?? -1;
          if (attachmentIdx < 0) return;

          typedQueryClient.setQueryData(CarDetailsQueryKeys.details, {
            ...car,
            notes: car.notes.with(noteIdx, { ...note, attachments: note.attachments?.toSpliced(attachmentIdx, 1) })
          });
        }
      }
    ];

    return [...carListeners, ...carNoteListeners, ...carNoteAttachmentListeners];
  }, [id]);

  const { data, isFetching, error } = useRealTimeTypedQuery({
    queryKey: CarDetailsQueryKeys.details,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, { id }, baseUrl)).data,
    listeners,
    select: groupTruckTyres
  });

  return {
    car: data,
    loading: isFetching,
    error
  };
};
